import * as React from 'react';
import {IDriver, IVehicle, IVehicleLookup} from '../../../../../../data/api/models';
import {Col, ControlLabel, FormGroup, Row} from 'react-bootstrap';
import {Field, FieldArray} from 'redux-form';
import {
  isRequired,
  normalizeCurrency,
  normalizeYear,
  isNotShorterThan,
  validateDrivingRestriction,
} from '../../../../../../util/validators';
import FieldFormControl from '../../../../../../components/FieldFormControl';
import DVLAControl from '../../../../../../components/DVLAControl';
import SelectControl from '../../../../../../components/SelectControl';
import SpecifiedDrivers from '../SpecifiedDrivers';
import DriverNonEquineTrades from '../DriverNonEquineTrades';
import FaCheck from 'react-icons/fa/check';
import { get, forEach } from 'lodash';
import {weightBandingDetection} from '../../../../../../util/helpers';
import FaTrash from 'react-icons/fa/trash';
import { FormattedNumber } from 'react-intl';
import * as api from '../../../../../../data/api/api';

interface IVehicleProps {
  schemeId: string;
  index: number;
  deleteVehicle: any;
  removable: boolean;
  excessOverrides: any;
  vehicle: IVehicle;
  weightOptions: any;
  coverOptions: any;
  drivingRestrictionOptions: any;
  drivingRestrictionRules: any;
  extendedUseTypes: any;
  mileageOptions: any;
  ownershipOptions: any;
  trackerOptions: any;
  name: string;
  dispatch: any;
  change: any;
  renderDefinition: any;
  drivers: IDriver[];
  proposerName: string;
  proposerPostcode: string;
  duplicateRegistration: boolean;
}

interface IVehicleState {
  showNamedDrivers: boolean;
}

class Vehicle extends React.Component<IVehicleProps, IVehicleState> {
  public state = {
    showNamedDrivers: false,
  };

  public componentDidMount() {
    const { vehicle } = this.props;

    if (vehicle.drivingRestriction) {
      this.updateDrivingRestriction(vehicle.drivingRestriction);
    }
  }

  public componentWillReceiveProps(nextProps) {
    const { dispatch, change, name } = this.props;

    if (nextProps.vehicle.drivingRestriction !== this.props.vehicle.drivingRestriction) {
      this.updateDrivingRestriction(nextProps.vehicle.drivingRestriction);
    }
    if (nextProps.vehicle.showExtendedUse !== this.props.vehicle.showExtendedUse &&
      nextProps.vehicle.showExtendedUse === 'false') {
      this.clearExtendedUses();
    }
    if (nextProps.vehicle.proposerOwner !== this.props.vehicle.proposerOwner &&
      nextProps.vehicle.proposerOwner === 'true') {
      this.clearOwner();
    }
    if (nextProps.vehicle.proposerOvernight !== this.props.vehicle.proposerOvernight &&
      nextProps.vehicle.proposerOwner === 'true') {
      this.clearPostcode();
    }
    if (nextProps.vehicle.value !== this.props.vehicle.value) {
      if (nextProps.schemeId) {
        api.vehicleExcesses(nextProps.schemeId, [nextProps.vehicle])
          .then((result) => {
            forEach (result.meta, (vehicles, type) => {
              forEach (vehicles, (vehicle, index) => {
                dispatch(change(name + '.excessAdft', vehicle.excess_adft));
              });
            });
          });
      }
    }
  }

  private handleDeleteVehicle = (index) => (e) => {
    const { deleteVehicle } = this.props;
    deleteVehicle(index);
  }

  private completeLookup = (vehicleLookup: IVehicleLookup) => {
    const { dispatch, change, name } = this.props;

    dispatch(change(name + '.make', vehicleLookup.make));
    dispatch(change(name + '.model', vehicleLookup.model));
    dispatch(change(name + '.year', vehicleLookup.year));

    const weight = weightBandingDetection(vehicleLookup.weight / 1000);
    dispatch(change(name + '.weight', weight));
  }

  private checkSearch = () => {
    const { dispatch, change, name } = this.props;
    dispatch(change(name + '.registrationNumberSearched', true));
  }

  private clearExtendedUses = () => {
    const { dispatch, change, drivers, name } = this.props;

    dispatch(change(name + '.extendedUses', [false, false, false, false, false]));
    dispatch(change('nonEquineTrade', ''));
    dispatch(change(name + '.equineTradeExtendedUsers', []));
    dispatch(change(name + '.nonEquineTradeExtendedUsers', []));
    forEach(drivers, (driver, index) => {
      dispatch(change(`drivers[${index}].nonEquineTrade`, ''));
    });
  }

  private clearPostcode = () => {
    const { dispatch, change, name } = this.props;

    dispatch(change(name + '.postcode', ''));
  }

  private clearOwner = () => {
    const { dispatch, change, name } = this.props;

    dispatch(change(name + '.owner', ''));
    dispatch(change(name + '.ownerName', ''));
  }

  private updateDrivingRestriction = (newRestriction) => {
    const {drivers, vehicle, name, dispatch, change, drivingRestrictionRules} = this.props;

    // if the restriction does not include named drivers then remove all driver references from the vehicle
    if (drivingRestrictionRules[newRestriction]) {
      this.setState({ showNamedDrivers: true });
    } else {
      this.setState({ showNamedDrivers: false });
      dispatch(change(vehicle + '.drivers', []));
    }

    // if the restriction is insured based then auto add the proposer driver
    const proposerRestrictions = [
      'Insured only',
      'Insured and named',
      'Insured and Spouse',
    ];

    if (proposerRestrictions.includes(newRestriction)) {
      const vehicleDrivers = vehicle.drivers ? vehicle.drivers : [];
      let proposerId = '';
      let driverIndex = 0;
      let driverVehicles = [];
      forEach(drivers, (driver, key) => {
        if (driver.proposer) {
          proposerId = driver.id;
          driverVehicles = driver.vehicles;
          driverIndex = key;
          return;
        }
      });
      let proposerAssigned = false;
      forEach(vehicleDrivers, (driver) => {
        if (driver.driver === proposerId) {
          proposerAssigned = true;
        }
      });
      if (!proposerAssigned) {
        const newDriverArray = [...vehicleDrivers, { driver: `${proposerId}` }];
        const newVehiclesArray = [...driverVehicles, vehicle.registrationNumber];
        dispatch(change(name + '.drivers', newDriverArray));
        dispatch(change(`drivers[${driverIndex}].vehicles`, newVehiclesArray));
      }
    }
  }

  private renderRegistrationNumber() {
    const { name, duplicateRegistration } = this.props;
    const normalizeRegistration = (value) => value && value.toUpperCase();
    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          Please enter the vehicle registration number:
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.registration_number'}>
            <Col sm={12} className="no-padding-left">
              <Col sm={12}>
                <Field
                  name={name + '.registrationNumber'}
                  component={DVLAControl}
                  onComplete={this.completeLookup}
                  validate={[ isRequired ]}
                  normalize={normalizeRegistration}
                  placeholder="Registration Number"
                  checkSearch={this.checkSearch}
                  duplicateRegistration={duplicateRegistration}
                />
              </Col>
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderMake() {
    const { name } = this.props;
    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          Vehicle Make:
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.make'}>
            <Col sm={12} className="no-padding-left">
              <Col sm={12}>
                <Field
                  name={name + '.make'}
                  component={FieldFormControl}
                  validate={[ isRequired ]}
                  placeholder="Make"
                />
              </Col>
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderModel() {
    const { name, renderDefinition } = this.props;
    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          Vehicle Model:
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.model'}>
            <Col sm={12} className="no-padding-left">
              <Col sm={12}>
                <Field
                  name={name + '.model'}
                  component={FieldFormControl}
                  validate={[ isRequired ]}
                  placeholder="Model"
                />
              </Col>
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderYear() {
    const { name } = this.props;
    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          Year of Make:
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.year'}>
            <Col sm={12} className="no-padding-left">
              <Col sm={4}>
                <Field
                  name={name + '.year'}
                  component={FieldFormControl}
                  normalize={normalizeYear}
                  validate={[
                    isRequired,
                    isNotShorterThan(3, 'Please enter a valid year'),
                  ]}
                  placeholder="YYYY"
                />
              </Col>
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderWeight() {
    const { name, renderDefinition, weightOptions } = this.props;
    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          {renderDefinition('b2c_vehicle_weight')}
          Maximum Gross Vehicle Weight:
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.weight'}>
            <Col sm={8} lg={6} className="no-padding-left">
              <Field
                type="text"
                name={name + '.weight'}
                component={SelectControl}
                validate={[ isRequired ]}
                options={weightOptions}
                placeholder="Please Select"
              />
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderValue() {
    const { name, renderDefinition } = this.props;
    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          {renderDefinition('b2c_vehicle_value')}
          What is the current market value of the vehicle?
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.value'}>
            <Col sm={12} className="no-padding-left">
              <Col sm={6}>
                <Field
                  addOn={'£'}
                  addOnClassName={['input-group-vehicle-value']}
                  currency={true}
                  type="inputGroup"
                  name={name + '.value'}
                  normalize={normalizeCurrency}
                  component={FieldFormControl}
                  validate={[ isRequired ]}
                  placeholder="0"
                />
              </Col>
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderCoverType() {
    const { name, coverOptions, renderDefinition } = this.props;
    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          {renderDefinition('b2c_cover_type')}
          What Cover do you require?
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.coverType'}>
            <Col sm={12} className="no-padding-left">
              <Field
                type="text"
                name={name + '.coverType'}
                component={SelectControl}
                validate={[ isRequired ]}
                options={coverOptions}
                placeholder="Please Select"
              />
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderDrivingRestriction() {
    const { name, drivingRestrictionOptions, vehicle } = this.props;
    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          Who will be driving the vehicle?
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.drivingRestriction'}>
            <Col sm={12} className="no-padding-left">
              <Field
                type="text"
                name={name + '.drivingRestriction'}
                component={SelectControl}
                validate={[
                  isRequired,
                  validateDrivingRestriction(vehicle.drivers ? vehicle.drivers.length : 0),
                ]}
                options={drivingRestrictionOptions}
                onChange={this.updateDrivingRestriction}
                placeholder="Please Select"
              />
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderDrivers() {
    const { name, dispatch, change, vehicle } = this.props;
    return (
      <Row>
        <Col smOffset={1} sm={11}>
          <FieldArray
            name={name + '.drivers'}
            baseName={name + '.drivers'}
            component={SpecifiedDrivers}
            dispatch={dispatch}
            change={change}
            fieldName={'drivers'}
            vehicle={vehicle}
          />
        </Col>
      </Row>
    );
  }

  private renderExtendedUses() {
    const { name, vehicle, extendedUseTypes, dispatch, change } = this.props;

    const showProposerNonEquineTrade = get(vehicle.extendedUses, 1, false);
    const showDriverEquineTrade = get(vehicle.extendedUses, 2, false);
    const showDriverNonEquineTrade = get(vehicle.extendedUses, 3, false);

    return (
      <>
        <Row>
          <Col smOffset={1} componentClass={ControlLabel} sm={6} style={{ marginBottom: '15px' }}>
            This quote is to cover use for Social, domestic and pleasure, including travel to and/or from the your place
            of work and/or study, are there any extended use requirements?
          </Col>
          <Col sm={5}>
            <Col sm={12}>
              <Row className="button-group-container">
                <Col sm={6} className="button-group-btn-container no-padding-horizontal">
                  <Field
                    name={name + '.showExtendedUse'}
                    type="radio"
                    value="false"
                    component={FieldFormControl}
                    validate={[isRequired]}
                  >
                    <div className={'button-group-btn'}>
                      No
                      <FaCheck/>
                    </div>
                  </Field>
                </Col>
                <Col sm={6} className="button-group-btn-container no-padding-horizontal">
                  <Field
                    name={name + '.showExtendedUse'}
                    type="radio"
                    value="true"
                    component={FieldFormControl}
                    validate={[isRequired]}
                  >
                    <div className={'button-group-btn'}>
                      Yes
                      <FaCheck/>
                    </div>
                  </Field>
                </Col>
              </Row>
            </Col>
          </Col>
        </Row>
        {vehicle.showExtendedUse === 'true' && (
          <>
            <Row>
              <Col smOffset={1} componentClass={ControlLabel} sm={6}>
                Do you need to add Extended Use?
              </Col>
              <Col sm={5}>
                {
                  extendedUseTypes.map((use, key) => (
                    <div key={key}>
                      <FormGroup controlId={`vehicle.extended_uses.${key}`}>
                        <div className="checkbox-payment">
                          <Field
                            name={`${name}.extendedUses[${key}]`}
                            type="checkbox"
                            component={FieldFormControl}
                          >
                            <div />
                            <span>{use}</span>
                          </Field>
                        </div>
                      </FormGroup>
                    </div>
                  ))
                }
              </Col>
            </Row>
            {showProposerNonEquineTrade && (
              <Row>
                <Col smOffset={1} componentClass={ControlLabel} sm={6}>
                  What is your non-equine trade?
                </Col>
                <Col sm={5}>
                  <FormGroup controlId={'vehicle.non_equine_trade'}>
                    <Col sm={12} className="no-padding-left">
                      <Col sm={12}>
                        <Field
                          name={'nonEquineTrade'}
                          component={FieldFormControl}
                          validate={[ isRequired ]}
                          placeholder="Non-equine trade"
                        />
                      </Col>
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
            )}
            {showDriverEquineTrade && (
              <Row>
                <Col smOffset={1} sm={11}>
                  <p>Please provide the name of driver(s) that need business use for their equine trade.</p>
                  <FieldArray
                    name={name + '.equineTradeExtendedUsers'}
                    baseName={name + '.equineTradeExtendedUsers'}
                    component={SpecifiedDrivers}
                    dispatch={dispatch}
                    change={change}
                  />
                </Col>
              </Row>
            )}
            {showDriverNonEquineTrade && (
              <Row>
                <Col smOffset={1} sm={11}>
                  <p>Please provide the name of driver(s) that need business use for their non-equine trade.</p>
                  <FieldArray
                    name={name + '.nonEquineTradeExtendedUsers'}
                    baseName={name + '.nonEquineTradeExtendedUsers'}
                    component={SpecifiedDrivers}
                    dispatch={dispatch}
                    change={change}
                  />
                  {vehicle.nonEquineTradeExtendedUsers.length > 0 && (
                    <>
                      <p>What is the trade of the named non-equine driver(s)?</p>
                      <DriverNonEquineTrades extendedUsers={vehicle.nonEquineTradeExtendedUsers} />
                    </>
                  )}
                </Col>
              </Row>
            )}
          </>
        )}
      </>
    );
  }

  private renderGreenCard() {
    const { name, renderDefinition } = this.props;

    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          {renderDefinition('b2c_vehicle_green_card')}
          Do you require Annual Green card?
        </Col>
        <Col sm={5}>
          <Col sm={12}>
            <Row className="button-group-container">
              <Col sm={6} className="button-group-btn-container no-padding-horizontal">
                <Field
                  name={name + '.greenCard'}
                  type="radio"
                  value="false"
                  component={FieldFormControl}
                  validate={[isRequired]}
                >
                  <div className={'button-group-btn'}>
                    No
                    <FaCheck/>
                  </div>
                </Field>
              </Col>
              <Col sm={6} className="button-group-btn-container no-padding-horizontal">
                <Field
                  name={name + '.greenCard'}
                  type="radio"
                  value="true"
                  component={FieldFormControl}
                  validate={[isRequired]}
                >
                  <div className={'button-group-btn'}>
                    Yes
                    <FaCheck/>
                  </div>
                </Field>
              </Col>
            </Row>
          </Col>
        </Col>
      </Row>
    );
  }

  private renderMileage() {
    const { name, vehicle, mileageOptions, renderDefinition } = this.props;

    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          {renderDefinition('b2c_vehicle_mileage')}
          What will be the annual mileage of {vehicle.registrationNumber}?
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.mileage'}>
            <Col sm={12} className="no-padding-left">
              <Field
                type="text"
                name={name + '.mileage'}
                component={SelectControl}
                validate={[ isRequired ]}
                options={mileageOptions}
                placeholder="Please Select"
              />
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderOwner() {
    const { name, vehicle, proposerName, ownershipOptions } = this.props;

    const showOwner = vehicle.proposerOwner === 'false';

    return (
      <>
        <Row>
          <Col smOffset={1} componentClass={ControlLabel} sm={6}>
            Is {proposerName} the registered owner of {vehicle.registrationNumber}?
          </Col>
          <Col sm={5}>
            <Col sm={12}>
              <Row className="button-group-container">
                <Col sm={6} className="button-group-btn-container no-padding-horizontal">
                  <Field
                    name={name + '.proposerOwner'}
                    type="radio"
                    value="false"
                    component={FieldFormControl}
                    validate={[isRequired]}
                  >
                    <div className={'button-group-btn'}>
                      No
                      <FaCheck/>
                    </div>
                  </Field>
                </Col>
                <Col sm={6} className="button-group-btn-container no-padding-horizontal">
                  <Field
                    name={name + '.proposerOwner'}
                    type="radio"
                    value="true"
                    component={FieldFormControl}
                    validate={[isRequired]}
                  >
                    <div className={'button-group-btn'}>
                      Yes
                      <FaCheck/>
                    </div>
                  </Field>
                </Col>
              </Row>
            </Col>
          </Col>
        </Row>
        {showOwner && (
          <>
            <Row>
              <Col smOffset={1} componentClass={ControlLabel} sm={6}>
                Please confirm who is the registered owner of {vehicle.registrationNumber}:
              </Col>
              <Col sm={5}>
                <FormGroup controlId={'vehicle.owner'}>
                  <Col sm={12} className="no-padding-left">
                    <Field
                      type="text"
                      name={name + '.owner'}
                      component={SelectControl}
                      validate={[ isRequired ]}
                      options={ownershipOptions}
                      placeholder="Please Select"
                    />
                  </Col>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col smOffset={1} componentClass={ControlLabel} sm={6}>
                Name of Owner:
              </Col>
              <Col sm={5}>
                <FormGroup controlId={'vehicle.owner_name'}>
                  <Col sm={12} className="no-padding-left">
                    <Col sm={12}>
                      <Field
                        name={name + '.ownerName'}
                        component={FieldFormControl}
                        validate={[ isRequired ]}
                        placeholder="Name"
                      />
                    </Col>
                  </Col>
                </FormGroup>
              </Col>
            </Row>
          </>
        )}
      </>
    );
  }

  private renderPostcode() {
    const { name, vehicle, proposerPostcode } = this.props;

    const showPostcode = vehicle.proposerOvernight === 'false';

    return (
      <>
        <Row>
          <Col smOffset={1} componentClass={ControlLabel} sm={6}>
            Is {vehicle.registrationNumber} kept overnight at {proposerPostcode}?
          </Col>
          <Col sm={5}>
            <Col sm={12}>
              <Row className="button-group-container">
                <Col sm={6} className="button-group-btn-container no-padding-horizontal">
                  <Field
                    name={name + '.proposerOvernight'}
                    type="radio"
                    value="false"
                    component={FieldFormControl}
                    validate={[isRequired]}
                  >
                    <div className={'button-group-btn'}>
                      No
                      <FaCheck/>
                    </div>
                  </Field>
                </Col>
                <Col sm={6} className="button-group-btn-container no-padding-horizontal">
                  <Field
                    name={name + '.proposerOvernight'}
                    type="radio"
                    value="true"
                    component={FieldFormControl}
                    validate={[isRequired]}
                  >
                    <div className={'button-group-btn'}>
                      Yes
                      <FaCheck/>
                    </div>
                  </Field>
                </Col>
              </Row>
            </Col>
          </Col>
        </Row>
        {showPostcode && (
          <Row>
            <Col smOffset={1} componentClass={ControlLabel} sm={6}>
              Please provide the postcode for where {vehicle.registrationNumber} is kept overnight:
            </Col>
            <Col sm={5}>
              <FormGroup controlId={'vehicle.postcode'}>
                <Col sm={12} className="no-padding-left">
                  <Col sm={12}>
                    <Field
                      name={name + '.postcode'}
                      component={FieldFormControl}
                      validate={[ isRequired ]}
                      placeholder="Postcode"
                    />
                  </Col>
                </Col>
              </FormGroup>
            </Col>
          </Row>
        )}
      </>
    );
  }

  private renderTracker() {
    const { name, vehicle, trackerOptions } = this.props;

    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          Is {vehicle.registrationNumber} fitted with a Tracker?
        </Col>
        <Col sm={5}>
          <FormGroup controlId={'vehicle.tracker'}>
            <Col sm={12} className="no-padding-left">
              <Field
                type="text"
                name={name + '.tracker'}
                component={SelectControl}
                validate={[ isRequired ]}
                options={trackerOptions}
                placeholder="Please Select"
              />
            </Col>
          </FormGroup>
        </Col>
      </Row>
    );
  }

  private renderExcess() {
    const { index, vehicle, excessOverrides } = this.props;

    let excess = vehicle.excessAdft;
    if (get(excessOverrides, `vehicle.horseboxes[${index}].excess_adft`)) {
      excess = get(excessOverrides, `vehicle.horseboxes[${index}].excess_adft`);
    }

    return (
      <Row>
        <Col smOffset={1} componentClass={ControlLabel} sm={6}>
          Additional Vehicle ADFT Excess
        </Col>
        <Col sm={5}>
          <FormattedNumber
            value={excess}
            style="currency"
            currency="GBP"
          />
        </Col>
      </Row>
    );
  }

  public render() {
    const { index, vehicle, removable } = this.props;
    const { showNamedDrivers } = this.state;
    if (vehicle.make === 'UNKNOWN') {
      vehicle.make = '';
    }
    if (vehicle.model === 'UNKNOWN') {
      vehicle.model = '';
    }

    return (
      <div className="sub-title">
        <Row>
          <Col sm={1}>
            <img src={require('../../../../img/vehicle.svg')} alt="" className="img-responsive padding-right" />
          </Col>
          <Col sm={10} className="vehicle-info-centre-subtitle">
            <h3>Horsebox {index + 1}</h3>
          </Col>
          {removable && (
            <Col sm={1}>
              <h3>
                <div
                  className="pointer"
                  onClick={this.handleDeleteVehicle(index)}
                >
                  <FaTrash />
                </div>
              </h3>
            </Col>
          )}
        </Row>
        {this.renderRegistrationNumber()}
        {(vehicle.registrationNumberSearched || vehicle.make) && (
          <>
            {this.renderMake()}
          </>
        )}
        {vehicle.make && (
          <>
            {this.renderModel()}
          </>
        )}
        {vehicle.model && (
          <>
            {this.renderYear()}
          </>
        )}
        {vehicle.year && (
          <>
            {this.renderWeight()}
          </>
        )}
        {vehicle.weight && (
          <>
            {this.renderValue()}
          </>
        )}
        {vehicle.value && (
          <>
            {this.renderCoverType()}
          </>
        )}
        {vehicle.coverType && (
          <>
            {this.renderDrivingRestriction()}
          </>
        )}
        {showNamedDrivers && (
          <>
            {this.renderDrivers()}
          </>
        )}
        {vehicle.drivingRestriction && (
          <>
            {this.renderExtendedUses()}
          </>
        )}
        {vehicle.showExtendedUse && (
          <>
            {this.renderGreenCard()}
          </>
        )}
        {vehicle.greenCard && (
          <>
            {this.renderMileage()}
          </>
        )}
        {vehicle.mileage && (
          <>
            {this.renderOwner()}
          </>
        )}
        {vehicle.proposerOwner && (
          <>
            {this.renderPostcode()}
          </>
        )}
        {vehicle.proposerOvernight && (
          <>
            {this.renderTracker()}
          </>
        )}
        {!!vehicle.excessAdft && (
          <>
            {this.renderExcess()}
          </>
        )}
      </div>
    );
  }
}

export default Vehicle;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/Vehicles/components/Vehicle/index.tsx